import {
  RelatorioPrestacaoContasItem,
  TipoItemRelatorioPrestacaoContas,
} from "features/relatorios/prestacao-contas/typings";
import {
  RelatorioIdEnum,
  RelatorioInadimplentesFields,
} from "core/features/relatorios/typings";
import PDFRelatorioReceitas from "ui/pages/Admin/Relatorios/Financeiro/RelatorioReceitas/components/PDFRelatorio";
import PDFRelatorioDemonstrativoReceitaDespesa from "ui/pages/Admin/Relatorios/Financeiro/DemonstrativoReceitasDespesas/components/PDFRelatorio";
import PDFRelatorioDemonstrativoReceitaDespesaAnual from "ui/pages/Admin/Relatorios/Financeiro/DemonstrativoReceitasDespesasAnual/components/PDFRelatorio";
import PDFRelatorioInadimplentes from "ui/pages/Admin/Relatorios/Financeiro/Inadimplentes/components/PDFRelatorio/index";
import PDFRelatorioDespesa from "ui/pages/Admin/Relatorios/Financeiro/RelatorioDespesa/components/PDFRelatorio/index";
import { getDateFromTipoFiltroData } from "core/features/relatorios/prestacao-contas/util/getDateFromTipoData";
import PDFCapa from "components/PDF/Capa";
import http from "infra/http";
import moment from "moment";
import {
  RelatorioReceitasFields,
  RelatorioDespesaFields,
  RelatorioContasComComposicaoFields,
} from "core/features/relatorios/typings";
import { showErrorAxios, showError } from "infra/helpers/alerta";
import { pdf, Document } from "@react-pdf/renderer";
import PDFRelatorioMovimentacaoFinanceira from "ui/components/RelatorioMovimentacaoFinanceira/components/PDFRelatorio";
import { getPeriodoRelatorio } from "core/features/relatorios/prestacao-contas/util/getPeriodoRelatorio";
import { logError } from "infra/packages/@sentry/logError";
import { getRelatorioDespesasComAnexosBlob } from "ui/pages/Admin/Relatorios/Financeiro/RelatorioDespesa/components/util/helper";
import PDFRelatorioBalancoFinanceiro from "./../../BalancoFinanceiro/components/PDFRelatorio";
import PDFRelatorioComposicaoContas from "../../../../../../components/Relatorio/RelatorioComposicaoContas/PDFRelatorio/index";
import PDFRelatorioComposicaoReceitasHorizontal from "./../../RelatorioComposicaoReceitasHorizontal/components/PDFRelatorio";
import { OperacaoEnum } from "resources/enums/conta";

export async function makeBlobOrAttachmentUrlList(
  items: Partial<RelatorioPrestacaoContasItem & { mes?: string }>[],
  logoUrl: string,
  onProgress?: (i: number, total: number) => any
) {
  const blobOrAttachmentList: (Blob | string)[] = [];

  try {
    for (let i = 0; i < items.length; i++) {
      onProgress?.(i, items.length);
      const item = items[i];
      const result = await getBlobAndAttachments(item, logoUrl);

      if (result?.blob) {
        blobOrAttachmentList.push(result.blob);
      }

      if (result?.attachments) {
        blobOrAttachmentList.push(...result.attachments);
      }
    }
  } catch (err) {
    logError(err);
    if ((err as any)?.response) {
      showErrorAxios(err);
    } else {
      showError("Ocorreu um erro ao exportar o PDF.");
    }
  }

  return blobOrAttachmentList;
}

export async function getBlobAndAttachments(
  item: Partial<RelatorioPrestacaoContasItem> & { mes?: string },
  logoUrl: string
) {
  const dates = getDateFromTipoFiltroData(
    item.tipoFiltroData,
    item.parametros?.filtrosAdicionais,
    item.mes
  );

  const [dataInicio, dataFim] = [dates?.dataInicio, dates?.dataFim];

  const periodo = getPeriodoRelatorio(dataInicio, dataFim);

  if (item.tipoItem === TipoItemRelatorioPrestacaoContas.Capa) {
    return {
      blob: await pdf(
        <Document>
          <PDFCapa
            dataInicio={dataInicio}
            dataFim={dataFim}
            logoUrl={logoUrl}
            orientation={item.orientacao ?? undefined}
            title={item.titulo}
          />
        </Document>
      ).toBlob(),
    };
  }

  if (
    item.tipoItem === TipoItemRelatorioPrestacaoContas.PdfExterno &&
    item.arquivo
  ) {
    return {
      attachments: [item.arquivo.referencia],
    };
  }

  const itemRelatorioPdfUrl = item.relatorioPdfUrl;

  if (item.relatorioId && itemRelatorioPdfUrl) {
    const Component = mapRelatorioIdToComponent[item.relatorioId];
    const ano = moment(dataFim).format("YYYY");
    const assinaturas = item.parametros?.assinaturas;

    const { data } = await http.get(itemRelatorioPdfUrl!, {
      params: {
        dataInicio, // As apis devem seguir esse padrão de filtro, ou por dataInicio/dataFim ou por ano.
        dataFim,
        ano,
        ...item.parametros?.filtrosAdicionais,
      },
    });

    switch (item.relatorioId) {
      case RelatorioIdEnum.Demonstrativo:
        const TypedComponentDemonstrativo =
          Component as typeof PDFRelatorioDemonstrativoReceitaDespesa;
        return {
          blob: await pdf(
            <TypedComponentDemonstrativo
              data={{ ...data, assinaturas }}
              operacao={item.parametros?.filtrosAdicionais?.operacao}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.BalancoFinanceiro:
        const TypedComponentBalancoFinanceiro =
          Component as typeof PDFRelatorioBalancoFinanceiro;
        return {
          blob: await pdf(
            <TypedComponentBalancoFinanceiro data={{ ...data, assinaturas }} />
          ).toBlob(),
        };

      case RelatorioIdEnum.DemonstrativoAnual:
        const TypedComponentDemonstrativoAnual =
          Component as typeof PDFRelatorioDemonstrativoReceitaDespesaAnual;
        return {
          blob: await pdf(
            <TypedComponentDemonstrativoAnual
              data={{ ...data, ano, assinaturas }}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.Inadimplentes:
        const TypedComponentInadimplentes =
          Component as typeof PDFRelatorioInadimplentes;
        const colunasInadimplentes = (item.parametros?.colunas ??
          []) as RelatorioInadimplentesFields[];

        return {
          blob: await pdf(
            <TypedComponentInadimplentes
              data={{ ...data, assinaturas }}
              selectedFields={colunasInadimplentes}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.Receitas:
        const TypedComponentReceitas = Component as typeof PDFRelatorioReceitas;
        const colunasReceitas = (item.parametros?.colunas ??
          []) as RelatorioReceitasFields[];
        return {
          blob: await pdf(
            <TypedComponentReceitas
              data={{ ...data, assinaturas }}
              renderProprietario={colunasReceitas.includes("nomeCondomino")}
              renderResumoCategorias={colunasReceitas.includes(
                "resumoContaCategoria"
              )}
              renderUnidade={colunasReceitas.includes("unidade")}
              fields={colunasReceitas}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.TotalDeDespesas:
        const colunasDespesas = (item.parametros?.colunas ??
          []) as RelatorioDespesaFields[];

        if (colunasDespesas.includes("anexos")) {
          const blob = await getRelatorioDespesasComAnexosBlob(
            { ...data, assinaturas },
            colunasDespesas
          );

          return {
            blob,
          };
        } else {
          const TypedComponentDespesas =
            Component as typeof PDFRelatorioDespesa;

          const colunasDespesas = (item.parametros?.colunas ??
            []) as RelatorioDespesaFields[];
          return {
            blob: await pdf(
              <TypedComponentDespesas
                {...data}
                selectedFields={colunasDespesas}
                assinaturas={assinaturas}
                periodo={periodo}
              />
            ).toBlob(),
          };
        }

      case RelatorioIdEnum.MovimentacaoFinanceira:
        const TypedComponentMovimentacao =
          Component as typeof PDFRelatorioMovimentacaoFinanceira;
        return {
          blob: await pdf(
            <TypedComponentMovimentacao
              {...data}
              assinaturas={assinaturas}
              periodo={periodo}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.ReceitasComComposicao:
        const TypedComponentComposicaoReceitas =
          Component as typeof PDFRelatorioComposicaoContas;
        const colunasRelatorioReceitasComComposicao = (item.parametros
          ?.colunas ?? []) as RelatorioContasComComposicaoFields[];

        return {
          blob: await pdf(
            <TypedComponentComposicaoReceitas
              operacao={OperacaoEnum.Receita}
              data={{
                ...data,
                assinaturas: assinaturas,
                periodo: periodo,
              }}
              fields={colunasRelatorioReceitasComComposicao}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.ComposicaoReceitasHorizontal:
        const TypedComponentComposicaoReceitasHorizontal =
          Component as typeof PDFRelatorioComposicaoReceitasHorizontal;
        return {
          blob: await pdf(
            <TypedComponentComposicaoReceitasHorizontal
              data={{
                ...data,
                assinaturas: assinaturas,
                periodo: periodo,
              }}
            />
          ).toBlob(),
        };

      case RelatorioIdEnum.DespesasComComposicao:
        const TypedComponentComposicaoDespesa =
          Component as typeof PDFRelatorioComposicaoContas;
        const colunasRelatorioDespesasComComposicao = (item.parametros
          ?.colunas ?? []) as RelatorioContasComComposicaoFields[];

        return {
          blob: await pdf(
            <TypedComponentComposicaoDespesa
              operacao={OperacaoEnum.Despesa}
              data={{
                ...data,
                assinaturas: assinaturas,
                periodo: periodo,
              }}
              fields={colunasRelatorioDespesasComComposicao}
            />
          ).toBlob(),
        };
    }
  }
}

const mapRelatorioIdToComponent = {
  [RelatorioIdEnum.Demonstrativo]: PDFRelatorioDemonstrativoReceitaDespesa,
  [RelatorioIdEnum.DemonstrativoAnual]:
    PDFRelatorioDemonstrativoReceitaDespesaAnual,
  [RelatorioIdEnum.Inadimplentes]: PDFRelatorioInadimplentes,
  [RelatorioIdEnum.Receitas]: PDFRelatorioReceitas,
  [RelatorioIdEnum.TotalDeDespesas]: PDFRelatorioDespesa,
  [RelatorioIdEnum.MovimentacaoFinanceira]: PDFRelatorioMovimentacaoFinanceira,
  [RelatorioIdEnum.BalancoFinanceiro]: PDFRelatorioBalancoFinanceiro,
  [RelatorioIdEnum.ReceitasComComposicao]: PDFRelatorioComposicaoContas,
  [RelatorioIdEnum.DespesasComComposicao]: PDFRelatorioComposicaoContas,
  [RelatorioIdEnum.ComposicaoReceitasHorizontal]:
    PDFRelatorioComposicaoReceitasHorizontal,
  [RelatorioIdEnum.Email]: null,
  [RelatorioIdEnum.Unidades]: null,
  [RelatorioIdEnum.Reservas]: null,
};
